import { ensureNN, toNum, toStr } from "@agrigate/ts-utils/lib/coercions";
import { formatQuantityDigits, inferSettlementUnit } from "./formatters";
export const getSkuQuantityTotals = (sku, quantity) => {
    if (sku?.weightKg == null && sku?.batchSize == null) {
        throw new Error("SKU must have either weightKg or batchSize set");
    }
    return {
        weightKg: sku?.weightKg != null ? quantity * toNum(sku.weightKg) : undefined,
        numBatches: sku?.batchSize != null ? quantity : undefined,
        batchSize: sku?.batchSize,
    };
};
export const mergeQuantity = (next, prev) => {
    if (!prev)
        return next;
    if (!next)
        return prev;
    if (prev._type === "kg" && next._type === "kg") {
        return {
            _type: "kg",
            weightKg: prev.weightKg + next.weightKg,
        };
    }
    if (prev._type === "batches" &&
        next._type === "batches" &&
        prev.batchSize === next.batchSize) {
        return {
            _type: "batches",
            numBatches: prev.numBatches + prev.numBatches,
            batchSize: prev.batchSize,
        };
    }
    throw new Error("Unable to merge incompatible quantities");
};
export const diffQuantity = (base, ...deductions) => {
    const final = { ...base };
    for (const deduction of deductions) {
        if (!deduction)
            continue;
        if (deduction._type === "kg" && final._type === "kg") {
            final.weightKg -= deduction.weightKg;
            continue;
        }
        if (deduction._type === "batches" && final._type === "batches") {
            final.numBatches -= deduction.numBatches;
            continue;
        }
        throw new Error("Unable to subtract incompatible quantities");
    }
    return final;
};
export const isQuantityEmpty = (qty) => {
    if (qty?._type === "kg")
        return qty.weightKg <= 0;
    if (qty?._type === "batches")
        return qty.numBatches <= 0;
    return true;
};
export const getQuantityInput = (pq) => {
    if (!pq)
        return "";
    switch (pq._type) {
        case "kg":
            return pq.weightKg ?? "";
        case "batches":
            return pq.numBatches;
    }
};
export const getProduceQuantity = (weightKg, numBatches, batchSize) => {
    if (numBatches) {
        return {
            _type: "batches",
            numBatches: toNum(numBatches),
            batchSize: batchSize ?? 1,
        };
    }
    if (weightKg == null)
        return null;
    return {
        _type: "kg",
        weightKg: toNum(weightKg),
    };
};
/* @deprecated */
export const getCurrentProduceAmount = (row) => {
    if (row?.amountPerBatch)
        return +toNum(row.amountPerBatch).toFixed(2);
    if (row?.amountPerKg)
        return +toNum(row.amountPerKg).toFixed(2);
    return undefined;
};
export const getAmountDifference = (baseAmount, revisedAmount) => {
    return revisedAmount - baseAmount;
};
export const getCurrentProduceQuantity = (weightKg, numBatches) => {
    if (numBatches)
        return toNum(numBatches);
    if (weightKg)
        return toNum(weightKg);
    return 0;
};
export const getBatchSize = (su) => ({
    DOZEN: 12,
    ITEM: 1,
    KG: null,
})[su];
const UnitToKgMultiplier = {
    KILOGRAM: 1,
    KG: 1,
    QUINTAL: 100,
    TON: 1000,
};
export const getUnitToKgMultiplier = (unit) => {
    const multiplier = UnitToKgMultiplier[unit];
    if (!multiplier)
        throw new Error(`Unsupported unit: ${unit}`);
    return multiplier;
};
export const convertKgToClosestUnit = (weightKg) => {
    const tonMultiplier = ensureNN(UnitToKgMultiplier.TON);
    const quintalMultiplier = ensureNN(UnitToKgMultiplier.QUINTAL);
    let inputValue;
    let inputUnit;
    if (weightKg >= tonMultiplier) {
        inputValue = weightKg / tonMultiplier;
        inputUnit = "TON";
    }
    else if (weightKg >= quintalMultiplier) {
        inputValue = weightKg / quintalMultiplier;
        inputUnit = "QUINTAL";
    }
    else {
        inputValue = weightKg;
        inputUnit = "KILOGRAM";
    }
    const roundedValue = formatQuantityDigits(inputValue);
    return {
        inputValue: roundedValue,
        inputUnit,
    };
};
export const convertToClosestUnit = (weight) => {
    if (weight._type === "batches") {
        return {
            ...weight,
            inputValue: toStr(weight.numBatches),
            inputUnit: inferSettlementUnit(weight),
        };
    }
    if (weight._type === "kg") {
        const { inputValue, inputUnit } = convertKgToClosestUnit(weight.weightKg);
        return {
            ...weight,
            inputValue,
            inputUnit,
        };
    }
};
export const getQuantityTypeShortForm = (quantityType) => {
    if (!quantityType)
        return undefined;
    const map = {
        QUINTAL: "ql",
        TON: "ton",
        KILOGRAM: "kg",
        KG: "kg",
        ITEM: "item",
        DOZEN: "dz",
    };
    return map[quantityType.toUpperCase()];
};
/** @deprecated */
export const convertKgWeight = (weightKg) => {
    const Ton = 1000;
    const Quintal = 100;
    let convertedWeight;
    let unit;
    if (weightKg >= Ton) {
        convertedWeight = weightKg / Ton;
        unit = "Ton";
    }
    else if (weightKg >= Quintal) {
        convertedWeight = weightKg / Quintal;
        unit = "Quintal";
    }
    else {
        convertedWeight = weightKg;
        unit = "Kg";
    }
    // Prevent too many decimals from showing
    // If no fractional part (common), show as whole number
    return `${convertedWeight.toFixed(2).replace(/\.00$/, "")} ${unit}`;
};
//# sourceMappingURL=quantity.js.map